import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Link,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';

const Show = ({ open, onClose, itemData, onRefresh }) => {
  if (!itemData) return null;

  const handleClose = () => {
    if (onRefresh) {
      onRefresh();
    }
    onClose(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid':
        return { backgroundColor: '#4caf50', color: 'white' };
      case 'invalid':
        return { backgroundColor: '#f44336', color: 'white' };
      case 'pending':
        return { backgroundColor: '#ff9800', color: 'white' };
      default:
        return { backgroundColor: '#9e9e9e', color: 'white' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'valid':
        return 'Valide';
      case 'invalid':
        return 'Invalide';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ color: '#666' }} />
          <Typography variant="h6">
            Détails de l'invitation
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Numéro d'invitation
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
            {itemData.number}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ID: {itemData.id}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Statut
          </Typography>
          <Chip
            label={getStatusLabel(itemData.statut)}
            size="small"
            sx={getStatusColor(itemData.statut)}
          />
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <ConfirmationNumberIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Ticket associé
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {itemData.ticket?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              ID: {itemData.ticket?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {itemData.ticket?.description}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Prix: {itemData.ticket?.price?.toLocaleString('fr-FR')} FCFA
            </Typography>
          </Box>
        </Box>

        {itemData.ticket?.activities && itemData.ticket.activities.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Activités associées
            </Typography>
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              {itemData.ticket.activities.map((activity, index) => (
                <Box key={activity.id} sx={{ mb: index < itemData.ticket.activities.length - 1 ? 2 : 0 }}>
                  <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {activity.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ID: {activity.id}
                  </Typography>
                  {activity.description && (
                    <Typography variant="body2" color="text.secondary">
                      {activity.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Destinataire
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {itemData.recipientName}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Email
          </Typography>
          <Typography variant="body1" gutterBottom>
            <Link href={`mailto:${itemData.recipientEmail}`} underline="hover">
              {itemData.recipientEmail}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Téléphone
          </Typography>
          <Typography variant="body1" gutterBottom>
            <Link href={`tel:${itemData.recipientPhone}`} underline="hover">
              {itemData.recipientPhone}
            </Link>
          </Typography>
        </Box>

        {itemData.notes && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <DescriptionIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Notes
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.notes}
            </Typography>
          </Box>
        )}

        {itemData.download_url && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Téléchargement de l'invitation
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Link 
                href={itemData.download_url}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: 'primary.main'
                }}
              >
                <EmailIcon sx={{ fontSize: 16 }} />
                Voir l'invitation PDF
              </Link>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = itemData.download_url;
                  link.download = `invitation-${itemData.number}.pdf`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.light',
                    color: 'primary.dark'
                  }
                }}
              >
                Télécharger PDF
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Créé par
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.createdBy?.name} ({itemData.createdBy?.email})
          </Typography>
        </Box>

        {itemData.client && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Client associé
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.client.name} ({itemData.client.email})
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

Show.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  itemData: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.string,
    statut: PropTypes.string,
    ticket: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      price: PropTypes.number,
      activities: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string
      }))
    }),
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    recipientPhone: PropTypes.string,
    notes: PropTypes.string,
    urlInvitation: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      email: PropTypes.string
    }),
    client: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      email: PropTypes.string
    })
  })
};

export default Show;
